import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search as SearchIcon, ExternalLink, Tag, Wallet, TrendingUp, FileText, Activity } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";

const SECTION_META = {
  "Expense Head — Vendor": { icon: Wallet, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  "Expense Head":          { icon: Wallet, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  "Revenue Head":          { icon: TrendingUp, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  "Business Overview":     { icon: FileText, color: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200" },
  "Key Operations":        { icon: Activity, color: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200" },
  "Our Responsibilities":  { icon: FileText, color: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200" },
  "Current Status":        { icon: Tag, color: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200" },
};

/**
 * Knowledge search — type a vendor / expense head / phrase and instantly
 * see which clients have it in their Business Profile.  Hits are sorted by
 * client name, then by section priority (vendor matches first).
 */
export default function Knowledge() {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ hits: [], client_count: 0, q: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef();

  const runSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setData({ hits: [], client_count: 0, q: query });
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/business-profiles/search", { params: { q: query.trim() } });
      setData(r.data);
    } catch (e) {
      const msg = parseApiError(e, "Search failed");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce the query → API call (220 ms feels snappy without being chatty).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, runSearch]);

  // Group hits by client for the rendered output.
  const groups = data.hits.reduce((acc, hit) => {
    (acc[hit.client_id] = acc[hit.client_id] || { client_id: hit.client_id, client_name: hit.client_name, hits: [] }).hits.push(hit);
    return acc;
  }, {});
  const grouped = Object.values(groups);

  const tooShort = q.trim().length > 0 && q.trim().length < 2;

  return (
    <div className="max-w-5xl mx-auto" data-testid="knowledge-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-[#0a2540]" style={{ fontFamily: "Outfit" }}>
          Knowledge
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Search vendors, expense heads, revenue heads and notes across every client's Business Profile.
        </p>
      </div>

      <div className="relative">
        <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Try a vendor name (e.g. BlueDart) or an expense head…"
          className="pl-9 pr-3 h-11 rounded-sm border-slate-200 text-sm"
          data-testid="knowledge-search-input"
        />
      </div>

      <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500 font-mono-data" data-testid="knowledge-status">
        {loading && <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" /> Searching…</span>}
        {!loading && data.q && q.trim().length >= 2 && (
          <span>
            {data.hits.length} match{data.hits.length === 1 ? "" : "es"} across {data.client_count} client{data.client_count === 1 ? "" : "s"}
          </span>
        )}
        {tooShort && <span className="text-slate-400">Type at least 2 characters…</span>}
        {error && <span className="text-red-600">{error}</span>}
      </div>

      <div className="mt-6 space-y-5" data-testid="knowledge-results">
        {!loading && q.trim().length >= 2 && grouped.length === 0 && (
          <div className="border border-dashed border-slate-200 rounded-sm py-10 px-4 text-center text-xs text-slate-400">
            No matches. Try a different phrase, or check that the client has been added to a Business Profile.
          </div>
        )}
        {grouped.map((g) => (
          <div key={g.client_id} className="border border-slate-200 rounded-sm" data-testid={`knowledge-client-${g.client_id}`}>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#0a2540]" style={{ fontFamily: "Outfit" }}>{g.client_name}</div>
                <div className="text-[10px] text-slate-400 font-mono-data mt-0.5">{g.hits.length} match{g.hits.length === 1 ? "" : "es"}</div>
              </div>
              <Link
                to={`/clients/${g.client_id}?tab=business`}
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0a2540]"
                data-testid={`knowledge-open-${g.client_id}`}
              >
                Open <ExternalLink size={11} />
              </Link>
            </div>
            <ul className="divide-y divide-slate-100">
              {g.hits.map((h, i) => {
                const meta = SECTION_META[h.section] || SECTION_META["Business Overview"];
                const Icon = meta.icon;
                return (
                  <li key={`${h.section}-${i}`} className="px-4 py-2.5 flex items-start gap-3" data-testid={`knowledge-hit-${g.client_id}-${i}`}>
                    <span className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-sm ${meta.bg} ${meta.border} border ${meta.color}`}>
                      <Icon size={11} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{h.section}</span>
                        {h.head_name && (
                          <span className="text-[11px] text-[#0a2540] font-medium">{h.head_name}</span>
                        )}
                      </div>
                      {h.matched_vendors?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {h.matched_vendors.map((v) => (
                            <Highlighted key={v} text={v} q={q} className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border ${meta.border} ${meta.bg} ${meta.color} text-[10px] font-mono-data`} />
                          ))}
                          {h.all_vendors?.filter((v) => !h.matched_vendors.includes(v)).map((v) => (
                            <span key={v} className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-slate-200 bg-slate-50 text-slate-500 text-[10px] font-mono-data">{v}</span>
                          ))}
                        </div>
                      )}
                      {h.snippet && (
                        <Highlighted text={h.snippet} q={q} as="div" className="mt-1 text-[12px] text-slate-700 leading-snug" />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// Tiny inline highlighter — splits on the query and bolds matches.
function Highlighted({ text, q, as: As = "span", className }) {
  if (!text || !q) return <As className={className}>{text}</As>;
  const safeQ = q.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
  const parts = text.split(new RegExp(`(${safeQ})`, "ig"));
  return (
    <As className={className}>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase()
          ? <mark key={i} className="bg-amber-100 text-amber-900 px-0.5 rounded-[2px]">{p}</mark>
          : <span key={i}>{p}</span>
      )}
    </As>
  );
}
